import { ISecurityGroup, IUser, SecurityPermissionLevel } from '../api-client/autogenerated';

/**
 * Pure permission predicates for bidding-related write actions.
 *
 * These intentionally live in a dependency-light module (types + enum only) so
 * they can be unit tested without pulling in the axios client / redux store, and
 * so components can reuse one canonical write-gate instead of re-deriving it.
 *
 * Admins (site admins and subscriber admins of the project) always pass. For
 * everyone else, the relevant module permission must be ALL (NUMBER_4). A VIEW
 * (NUMBER_0) user never passes.
 *
 * An archived project is view-only for everyone, admins included -- the API refuses these writes
 * outright there -- so it is checked ahead of the admin short-circuit.
 */

type ProjectLike = { subscriberId?: string | null; isArchived?: boolean | null } | null | undefined;

/**
 * What every control disabled by archiving says when hovered. One string so the explanation reads
 * the same wherever a user runs into it -- the alternative is a dozen near-identical sentences.
 */
export const ARCHIVED_PROJECT_TOOLTIP = 'This project is archived and can only be viewed.';

/** Whether the project is archived, and so view-only. Nullable column: only a set flag counts. */
export const isProjectArchived = (project?: { isArchived?: boolean | null } | null): boolean =>
  !!project?.isArchived;

const isAdminOfProject = (user: IUser, project: ProjectLike): boolean =>
  user.isSiteAdmin === true ||
  (!!project?.subscriberId && user.adminOfSubscriberId === project.subscriberId);

/** Whether the user may upload/replace a completed bid tabulation file. */
export const canUploadBidTabulation = (
  user?: IUser | null,
  security?: ISecurityGroup | null,
  project?: ProjectLike,
): boolean => {
  if (!user || project?.isArchived) return false;
  if (isAdminOfProject(user, project)) return true;
  return security?.bidTabulationPermission === SecurityPermissionLevel.NUMBER_4;
};

/** Whether the user may edit bid management setup (incl. deleting spec/drawing files). */
export const canEditBidManagement = (
  user?: IUser | null,
  security?: ISecurityGroup | null,
  project?: ProjectLike,
): boolean => {
  if (!user || project?.isArchived) return false;
  if (isAdminOfProject(user, project)) return true;
  return security?.bidManagementPermission === SecurityPermissionLevel.NUMBER_4;
};

/**
 * Why a completed bid tabulation cannot be uploaded yet, or undefined when it can.
 *
 * The same conditions the Bid Management button enforces -- bid setup exists, the bid date has
 * passed, and the user may upload -- in one place so the dashboard can say why the action is
 * unavailable rather than only that it is. Returning the reason rather than a boolean is the point:
 * "bids are still open" and "you lack permission" are not the same problem. Re-uploading over an
 * existing tabulation is allowed, as it is on Bid Management.
 */
export const getBidTabulationUploadBlockedReason = (
  user: IUser | null | undefined,
  security: ISecurityGroup | null | undefined,
  project: ProjectLike,
  bidSetup: { dueDate?: string | null } | null | undefined,
  now: Date = new Date(),
): string | undefined => {
  if (!bidSetup) return 'Complete Bid Setup before uploading a bid tabulation.';
  if (!bidSetup.dueDate || new Date(bidSetup.dueDate) >= now) {
    return 'Bids are still open. The tabulation can be uploaded once the bid date has passed.';
  }
  if (!canUploadBidTabulation(user, security, project)) {
    return 'You do not have permission to upload a completed bid tabulation.';
  }
  return undefined;
};

/**
 * Whether the "restrict submission to prime bidders only" bid setting blocks this
 * user from submitting a Bidder RFI or Substitution Request in the bidding portal.
 */
export const requiresPrimeBidderToSubmit = (
  bidSetup?: { restrictSubmissionToPrimeBidders?: boolean | null } | null,
  isPrimeBidder?: boolean,
): boolean => !!bidSetup?.restrictSubmissionToPrimeBidders && !isPrimeBidder;
